import { useQuery } from '@apollo/client/react';
import { useTheme } from '@emotion/react';
import { isBefore } from 'date-fns';
import { useSetAtom } from 'jotai';
import Router, { useRouter } from 'next/router';
import React, { useCallback, useEffect, useMemo } from 'react';

import { Chip, Dropdown, Flex } from '~/components/blocks';
import { NotificationBadge } from '~/components/layouts/SharedAppShell/NotificationBadge';
import { AppHeader } from '~/components/partials';
import {
  GetAccessPermissionDocument,
  GetAccessPermissionQuery,
  GetPractitionerNameDocument,
  GetPractitionerNameQuery,
  PractitionerNameFragment,
} from '~/graphql/generated/graphql';
import { getMe } from '~/graphql/utility';
import { useFirebase } from '~/hooks/use-firebase';
import { usePractitionerRole } from '~/hooks/use-practitioner-role';
import { useChannel } from '~/hooks/use-subscriptions';
import { clearJWT } from '~/localstrage/jwt';
import { permissionModalState } from '~/state/layouts/OrganizationAppShell/atoms';
import { logoutState, twoFactorAuthModalState } from '~/state/layouts/SharedAppShell/atoms';
import { openWithNoOpener } from '~/utils/window';

import { Plan } from './Plan';
import { useManageNotifications } from './use-manage-notifications';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const isPractitionerNameFragment = (node: any): node is PractitionerNameFragment => {
  return node?.__typename === 'Practitioner';
};

const getHasAllowedAccess = (data?: GetAccessPermissionQuery) => {
  if (!data) return false;

  const expiredAt = getMe(data)?.operatorAccessPermission?.expiresAt;

  if (!expiredAt) return false;

  const today = new Date();
  const expiredDate = new Date(expiredAt);

  return isBefore(today, expiredDate);
};

const getIsProxy = (data?: GetPractitionerNameQuery) => {
  if (!data) return false;
  if (!isPractitionerNameFragment(data.me)) return false;

  return data.me.isProxy;
};

export const Header = () => {
  const theme = useTheme();
  const router = useRouter();
  const resetTwoFactor = router.asPath.replace(router.pathname, '') === '#reset_two_factor';

  const { data } = useQuery(GetPractitionerNameDocument);
  const { auth } = useFirebase();
  const { data: permissionData } = useQuery(GetAccessPermissionDocument);
  const hasAllowedAccess = getHasAllowedAccess(permissionData);
  const { verifyRole } = usePractitionerRole();

  const setPermissionModalState = useSetAtom(permissionModalState);
  const setLogoutState = useSetAtom(logoutState);
  const setTwoFactorAuthModalState = useSetAtom(twoFactorAuthModalState);
  const { disconnect } = useChannel();
  const practitionerName = useMemo(() => {
    const me = data?.me;
    if (!isPractitionerNameFragment(me)) return '';

    return `${me.familyName} ${me.givenName}`;
  }, [data?.me]);
  const isProxyLogin = getIsProxy(data);

  const handleClickTerm = useCallback(
    () => openWithNoOpener('https://intercom.help/pharms/ja/articles/14317967'),
    [],
  );
  const handleClickPolicy = useCallback(
    () => openWithNoOpener('https://www.medley.jp/external-service.html'),
    [],
  );
  const handleOpenAccessPermissionModal = useCallback(
    () => setPermissionModalState({ isOpen: true }),
    [setPermissionModalState],
  );
  const handleClickCompany = useCallback(() => Router.push('/company'), []);
  const handleClickTwoFactor = useCallback(() => {
    setTwoFactorAuthModalState((_state) => ({
      ..._state,
      isOpen: true,
      currentPanel: 'introduction',
    }));
  }, [setTwoFactorAuthModalState]);
  const handleLogout = useCallback(async () => {
    setLogoutState({ isLogout: true });
    await disconnect();
    auth.signOut();
    clearJWT();
    window.location.href = '/login';
  }, [auth, disconnect, setLogoutState]);

  useManageNotifications();

  useEffect(() => {
    if (resetTwoFactor) {
      setTwoFactorAuthModalState((_state) => ({
        ..._state,
        isOpen: true,
        isReset: true,
        currentPanel: 'introduction',
      }));
      router.push('/');
    }
  }, [resetTwoFactor, router, setTwoFactorAuthModalState]);

  return (
    <AppHeader
      action={
        <Flex alignItems="center">
          <Flex marginRight={theme.space.m}>
            <NotificationBadge />
          </Flex>
          <Dropdown text={practitionerName} placement="bottom-end">
            <Dropdown.Menu>
              <Plan />
              <Dropdown.Item onClick={handleClickTerm}>利用規約</Dropdown.Item>
              <Dropdown.Item onClick={handleClickPolicy}>外部送信ポリシー</Dropdown.Item>
              {!isProxyLogin && (
                <Dropdown.Item onClick={handleOpenAccessPermissionModal}>
                  アクセス許可
                  {hasAllowedAccess && (
                    <Chip size="s" color="green" ml={theme.space.l}>
                      許可中
                    </Chip>
                  )}
                </Dropdown.Item>
              )}
              <Dropdown.Item onClick={handleClickTwoFactor}>二要素認証</Dropdown.Item>
              {verifyRole('company_admin') && (
                <Dropdown.Item onClick={handleClickCompany}>本部設定</Dropdown.Item>
              )}
              <Dropdown.Item onClick={handleLogout}>ログアウト</Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        </Flex>
      }
    />
  );
};
